/**
 * CriticalDamageTab — interactive reference for all 16 critical damage tables.
 * Damage type buttons on first row, body location buttons on second row,
 * single table displayed below.
 */

import React, { useState } from 'react';
import { CriticalDamageService, CriticalEffect } from '../../../services/CriticalDamageService';
import { DamageType } from '../../../types/dice';

type LocationKey = 'arm' | 'body' | 'head' | 'leg';

const DAMAGE_TYPES: { key: DamageType; label: string; color: string; activeColor: string }[] = [
  {
    key: DamageType.Impact,
    label: 'Impact',
    color: 'bg-surface-hover text-ink',
    activeColor: 'bg-surface-hover text-on-action'
  },
  {
    key: DamageType.Rending,
    label: 'Rending',
    color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    activeColor: 'bg-red-600 dark:bg-red-500 text-white'
  },
  {
    key: DamageType.Energy,
    label: 'Energy',
    color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    activeColor: 'bg-blue-600 dark:bg-blue-500 text-white'
  },
  {
    key: DamageType.Explosive,
    label: 'Explosive',
    color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    activeColor: 'bg-orange-600 dark:bg-orange-500 text-white'
  }
];

const LOCATIONS: { key: LocationKey; label: string; icon: string }[] = [
  { key: 'head', label: 'Head', icon: '🧠' },
  { key: 'body', label: 'Body', icon: '🫁' },
  { key: 'arm', label: 'Arm', icon: '💪' },
  { key: 'leg', label: 'Leg', icon: '🦵' }
];

function EffectRow({ entry }: { entry: CriticalEffect }) {
  return (
    <tr
      className={`border-b border-line ${entry.death ? 'bg-red-50 dark:bg-red-900/20' : entry.permanent ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}`}
    >
      <td className="px-3 py-2 text-center font-bold text-sm w-16">
        {entry.severity >= 10 ? '10+' : entry.severity}
      </td>
      <td className="px-3 py-2 text-sm text-ink">{entry.effect}</td>
      <td className="px-3 py-2 text-center text-sm w-20">
        {entry.fatigueRoll || (entry.fatigue > 0 ? entry.fatigue : '—')}
      </td>
      <td className="px-3 py-2 text-sm w-36">
        <div className="flex flex-wrap gap-1">
          {entry.conditions.map((c, i) => (
            <span key={i} className="px-1.5 py-0.5 text-xs rounded bg-surface-hover text-ink">
              {c}
            </span>
          ))}
          {entry.death && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 font-bold">
              ☠ Death
            </span>
          )}
          {entry.testOrDeath && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200">
              T or ☠
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

export const CriticalDamageTab: React.FC = () => {
  const [selectedType, setSelectedType] = useState<DamageType>(DamageType.Impact);
  const [selectedLocation, setSelectedLocation] = useState<LocationKey>('body');

  const entries = CriticalDamageService.getTable(selectedType, selectedLocation);
  const typeLabel = DAMAGE_TYPES.find((d) => d.key === selectedType)?.label || selectedType;
  const locLabel = LOCATIONS.find((l) => l.key === selectedLocation)?.label || selectedLocation;

  return (
    <div className="bg-surface border border-line rounded-lg p-6">
      <h2 className="text-lg font-bold text-ink mb-1">Critical Damage Tables</h2>
      <p className="text-sm text-ink-faint mb-4">
        When damage exceeds a target's wounds, consult the table for the damage type and hit
        location at the critical severity level.
      </p>

      {/* Damage type buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        {DAMAGE_TYPES.map((dt) => (
          <button
            key={dt.key}
            onClick={() => setSelectedType(dt.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedType === dt.key ? dt.activeColor : dt.color
            } hover:opacity-90`}
          >
            {dt.label}
          </button>
        ))}
      </div>

      {/* Location buttons */}
      <div className="flex flex-wrap gap-2 mb-5">
        {LOCATIONS.map((loc) => (
          <button
            key={loc.key}
            onClick={() => setSelectedLocation(loc.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              selectedLocation === loc.key
                ? 'bg-surface-hover text-on-action'
                : 'bg-surface-hover text-ink'
            } hover:opacity-90`}
          >
            {loc.icon} {loc.label}
          </button>
        ))}
      </div>

      {/* Table title */}
      <h3 className="text-base font-semibold text-ink mb-3">
        {typeLabel} Critical Effects — {locLabel}
      </h3>

      {/* Single table */}
      {entries.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-alt">
                <th className="px-3 py-2 text-xs font-semibold text-ink-dim w-16">Sev.</th>
                <th className="px-3 py-2 text-xs font-semibold text-ink-dim">Effect</th>
                <th className="px-3 py-2 text-xs font-semibold text-ink-dim w-20 text-center">
                  Fatigue
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-ink-dim w-36">Conditions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <EffectRow key={entry.severity} entry={entry} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-ink-faint italic">No data for this combination.</p>
      )}
    </div>
  );
};
